import React from 'react';
import { formatDisplayEmail } from '../../utils';
import { useTranslation } from 'react-i18next';
import {
  Table,
  Checkbox,
  Avatar,
  Group,
  Text,
  ScrollArea,
  Paper,
  Stack,
  Tooltip,
  Button,
  Badge,
  Pagination,
} from '@mantine/core';
import { User } from '@/pages/NewAdminConsole/services/permissionApi';
import { usePermissionControl } from '@/pages/NewAdminConsole/hooks/usePermissionControl';
import { useMediaQuery } from '@mantine/hooks';

// 权限颜色映射（与成员管理保持一致）
const getPermissionColor = (roleCode?: string): string => {
  switch (roleCode) {
    case 'ROLE_COMPANY_OWNER':
      return 'red';
    case 'ROLE_ADMIN':
      return 'orange';
    case 'ROLE_ADMIN_DEPARTMENT':
      return 'yellow';
    case 'ROLE_EDITOR':
      return 'blue';
    case 'ROLE_VIEWER':
      return 'green';
    default:
      return 'gray';
  }
};

// 获取权限文本翻译（与成员管理保持一致）
const getPermissionText = (roleCode: string | undefined, t: any) => {
  switch (roleCode) {
    case 'ROLE_COMPANY_OWNER':
      return t('ROLE_COMPANY_OWNER') || 'Company Owner';
    case 'ROLE_ADMIN':
      return t('ROLE_ADMIN') || 'Administrator';
    case 'ROLE_ADMIN_DEPARTMENT':
      return t('ROLE_ADMIN_DEPARTMENT') || 'Department Admin';
    case 'ROLE_EDITOR':
      return t('ROLE_EDITOR') || 'Editor';
    case 'ROLE_VIEWER':
      return t('ROLE_VIEWER') || 'Viewer';
    default:
      return t('ROLE_VIEWER') || 'Viewer';
  }
};

interface MemberListProps {
  members: User[];
  selectedMembers: string[];
  activePage: number;
  totalPage: number;
  onToggleAll: () => void;
  onToggleSelection: (memberId: number | string) => void;
  onAssignUser: (user: User[]) => void;
  onPageChange: (page: number) => void;
}

export const MemberList: React.FC<MemberListProps> = ({
  members,
  selectedMembers,
  activePage,
  totalPage,
  onToggleAll,
  onToggleSelection,
  onAssignUser,
  onPageChange,
}) => {
  const { t } = useTranslation();
  const permissionControl = usePermissionControl();
  const canAssignUser = permissionControl.canAssignUserToDepartment;
  const matches = useMediaQuery('(max-height: 600px)');

  const rows = members.map((member) => (
    <Table.Tr key={member.id} style={{ cursor: 'default' }}>
      <Table.Td>
        <Checkbox
          checked={selectedMembers.includes(member.id.toString())}
          disabled={!canAssignUser(member)}
          onChange={() => onToggleSelection(member.id)}
        />
      </Table.Td>

      <Table.Td>
        <Group gap="sm">
          <Avatar size="sm" color="blue"></Avatar>
          <Stack w={140}>
            <Tooltip label={member.name} position="top-start">
              <Text size="sm" fw={500} truncate="end">
                {member.name}
              </Text>
            </Tooltip>
            <Tooltip label={formatDisplayEmail(member.email)} position="top-start">
              <Text size="xs" c="dimmed">
                {formatDisplayEmail(member.email)}
              </Text>
            </Tooltip>
          </Stack>
        </Group>
      </Table.Td>

      <Table.Td>
        <Text size="sm" c="dimmed">
          {member.updatedAt}
        </Text>
      </Table.Td>

      <Table.Td>
        <Text size="sm" c="dimmed">
          {member.createdAt}
        </Text>
      </Table.Td>

      <Table.Td>
        <Badge size="sm" color={getPermissionColor(member.roleCode)}>
          {getPermissionText(member.roleCode, t)}
        </Badge>
      </Table.Td>

      <Table.Td>
        {/* <Group justify="end"> */}
        <Button size="sm" variant="filled" onClick={() => onAssignUser([member])} disabled={!canAssignUser(member)}>
          {t('adminConsole.newConsole.table.actions')}
        </Button>
        {/* </Group> */}
      </Table.Td>
    </Table.Tr>
  ));

  if (members.length === 0) {
    return (
      <Paper p="xl" radius="md" style={{ textAlign: 'center' }}>
        <Text c="dimmed">{t('adminConsole.newConsole.table.noData')}</Text>
      </Paper>
    );
  }

  const allSelectChecked = selectedMembers.length === members.length && members.length > 0;
  const indeterminate = selectedMembers.length > 0 && selectedMembers.length < members.length;
  const hasSelectableUser = members.some((member) => canAssignUser(member));

  return (
    <div style={{ overflow: 'auto', flex: 1 }}>
      <Paper radius="md">
        <ScrollArea h={matches ? 'calc(100vh - 330px)' : 'calc(100vh - 270px)'} type="never">
          <Table striped highlightOnHover layout="fixed">
            <Table.Thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
              <Table.Tr>
                <Table.Th style={{ width: 40 }}>
                  <Checkbox
                    checked={allSelectChecked}
                    indeterminate={indeterminate}
                    disabled={!hasSelectableUser}
                    onChange={onToggleAll}
                  />
                </Table.Th>
                <Table.Th>{t('adminConsole.newConsole.table.userInfo')}</Table.Th>
                <Table.Th>{t('adminConsole.newConsole.table.updateAt')}</Table.Th>
                <Table.Th>{t('adminConsole.newConsole.unassignedUsers.joinTime')}</Table.Th>
                <Table.Th>{t('adminConsole.newConsole.table.permissionLevel')}</Table.Th>
                <Table.Th w={200}>{t('adminConsole.newConsole.table.actions')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>
      <div className="w-full flex justify-center items-center mt-4">
        <Pagination value={activePage} onChange={onPageChange} total={totalPage} />
      </div>
    </div>
  );
};
